"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { getUserInfo } from "@/services/users.api";
import { PRO_PLAN } from "@/constant/plan.name";
import { SubscriptionStatusEnum } from "@/constant/subs.status";

interface AuthContextType {
  token: string | null;
  user: any;
  isAuthenticated: boolean;
  isProPlan: boolean;
  isSubscriptionInactive: boolean;
  setToken: (key: string) => void;
  setTokenSync: (key: string) => Promise<any>;
  setUser: (user: any) => void;
  setIsProPlan: (isProPlan: boolean) => void;
  setIsSubscriptionInactive: (isSubscriptionInactive: boolean) => void;
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setTokenStatus] = useState<string | null>(null);
  const [user, setUserStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProPlan, setIsProPlanStatus] = useState(false);
  const [isSubscriptionInactive, setIsSubscriptionInactive] = useState(false);

  const updateUserSession = async () => {
    try {
      const response = await getUserInfo();
      const user = response.data;
      setUserStatus(user);

      const plan = user.plan;
      const isProPlan = user && plan && plan.name === PRO_PLAN;
      setIsProPlanStatus(isProPlan);

      const subscriptions = user.subscriptions;
      if (subscriptions && subscriptions.length > 0) {
        const latestSubscription = subscriptions[0];
        const isInactive =
          latestSubscription.status === SubscriptionStatusEnum.Inactive;
        setIsSubscriptionInactive(isInactive);
      }
      return user;
    } catch (error: any) {
      console.error("Failed to fetch user info:", error);
      if (error.response && error.response.status === 401) {
        clearAuth();
      }
      if (error.response && error.response.status === 404) {
        return;
      }
      throw error;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const currentToken = token || localStorage.getItem("token");
      if (currentToken && !user) {
        if (!token) {
          setTokenStatus(currentToken);
        }
        await updateUserSession();
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [token, user]);

  const setToken = (token: string) => {
    localStorage.setItem("token", token);
    setTokenStatus(token);
  };

  const setTokenSync = async (token: string): Promise<any> => {
    localStorage.setItem("token", token);
    const user = await updateUserSession();
    setTokenStatus(token);
    return user;
  };

  const setUser = (user: any) => {
    setUserStatus(user);
  };

  const setIsProPlan = (isProPlan: boolean) => {
    setIsProPlanStatus(isProPlan);
  };

  const clearAuth = () => {
    localStorage.removeItem("token");
    setTokenStatus(null);
    setUserStatus(null);
    setIsProPlan(false);
  };

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        isAuthenticated,
        isProPlan,
        isSubscriptionInactive,
        setToken,
        setTokenSync,
        setUser,
        setIsProPlan,
        setIsSubscriptionInactive,
        clearAuth,
      }}
    >
      {isLoading ? null : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
